import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Link2, Send, FileText, Receipt } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const d = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join(".") : "–");
const eur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const KAN = { email: "E-Mail", telefon: "Telefon", whatsapp: "WhatsApp" };

const Punkt = ({ ok, label, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${ok ? "bg-[#E7EFE5] text-[#4F6650]" : "bg-[#F5F0EB] text-[#8E7B71]"}`}>
    {ok ? "✓" : "–"} {label}
  </span>
);

const pdfOeffnen = async (peId, pid) => {
  const r = await axios.get(`${API}/pflegeeinrichtungen/${peId}/einwilligung/pdf/${pid}`, { responseType: "blob" }).catch(() => null);
  if (!r) return toast.error("PDF nicht verfügbar");
  window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
};

export const EinwilligungEinrichtungBox = ({ peId }) => {
  const [s, setS] = useState(null);
  const laden = useCallback(() => axios.get(`${API}/pflegeeinrichtungen/${peId}/einwilligung`).then((r) => setS(r.data)).catch(() => setS(null)), [peId]);
  useEffect(() => { laden(); }, [laden]);
  if (!s) return null;
  const ew = s.einwilligung;
  const link = async (senden) => {
    if (senden && !window.confirm(`Einwilligungs-Mail jetzt wirklich an ${s.kontakt_email} senden?`)) return;
    try {
      const r = await axios.post(`${API}/pflegeeinrichtungen/${peId}/einwilligung/link`, { senden });
      if (senden) toast.success(`Gesendet an ${r.data.gesendet}`);
      else if (await navigator.clipboard?.writeText(r.data.link).then(() => true).catch(() => false)) toast.success("Link kopiert");
      else toast.message("Link unten in der Box – bitte von Hand kopieren");
      laden();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const widerrufen = async () => {
    const grund = window.prompt("Widerruf aller Einwilligungen erfassen – Grund / wie mitgeteilt:");
    if (!grund) return;
    await axios.put(`${API}/pflegeeinrichtungen/${peId}/einwilligung/widerruf`, { felder: ["email", "telefon", "whatsapp", "datenschutz"], grund })
      .then(() => { toast.success("Widerruf erfasst"); laden(); }).catch((e) => toast.error(e.response?.data?.detail || "Fehler"));
  };
  return (
    <Card className="card-custom mb-5" data-testid="einwilligung-einrichtung-box">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1"><ShieldCheck size={16} className="text-[#9A3B2E]" /><h3 className="font-semibold">Einwilligung Kontakt &amp; Datenschutz</h3></div>
        {ew ? (
          <div className="space-y-2 mt-2">
            <div className="flex flex-wrap gap-2">
              {Object.entries(KAN).map(([k, l]) => <Punkt key={k} ok={ew.werbung?.[k]} label={l} testid={`einwilligung-kanal-${k}`} />)}
              <Punkt ok={ew.datenschutz} label="Datenschutz" testid="einwilligung-datenschutz" />
            </div>
            <p className="text-xs text-[#8E7B71]" data-testid="einwilligung-unterzeichner">Unterschrieben von {ew.name} ({ew.funktion}) am {dt(ew.zeitpunkt)}</p>
          </div>
        ) : <p className="text-sm text-[#8E7B71] mt-1" data-testid="einwilligung-keine">Noch keine Einwilligung vorhanden.</p>}
        <div className="flex flex-wrap gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={() => link(false)} data-testid="einwilligung-link-kopieren"><Link2 size={14} className="mr-1" />Link kopieren</Button>
          <Button size="sm" variant="outline" disabled={!s.kontakt_email} onClick={() => link(true)} data-testid="einwilligung-link-senden"><Send size={14} className="mr-1" />Per E-Mail anfragen</Button>
          {ew && <Button size="sm" variant="ghost" onClick={widerrufen} data-testid="einwilligung-widerruf">Widerruf erfassen</Button>}
        </div>
        {s.link && <p className="text-xs text-[#8E7B71] mt-2 break-all select-all" data-testid="einwilligung-link-text">{s.link}</p>}
        {s.historie.length > 0 && (
          <div className="mt-3 border-t border-[#F6E3DA] pt-2 space-y-1" data-testid="einwilligung-historie">
            {s.historie.map((h, i) => (
              <div key={i} className="text-xs text-[#78716c] flex items-center gap-2">
                <span>{dt(h.zeitpunkt)} · {h.art === "einrichtung_widerruf" ? `Widerruf (${h.erfasst_von})` : `Einwilligung von ${h.name}`}</span>
                {h.protokoll_id && <button className="underline inline-flex items-center gap-1" onClick={() => pdfOeffnen(peId, h.protokoll_id)} data-testid={`einwilligung-pdf-${i}`}><FileText size={12} />PDF</button>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const rechnungPdf = async (id) => {
  const r = await axios.get(`${API}/rechnungen/${id}/pdf`, { responseType: "blob" }).catch(() => null);
  if (!r) return toast.error("PDF nicht verfügbar");
  window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
};

export const OffeneRechnungenBox = ({ rechnungen }) => {
  const summe = rechnungen.reduce((a, r) => a + r.offen, 0);
  const heute = new Date().toISOString().slice(0, 10);
  return (
    <Card className="card-custom mb-5" data-testid="offene-rechnungen-box">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2"><Receipt size={16} className="text-[#9A3B2E]" /><h3 className="font-semibold">Offene Rechnungen</h3></div>
          <span className="text-sm font-semibold" data-testid="offene-rechnungen-summe">{rechnungen.length} · {eur(summe)} offen</span>
        </div>
        {rechnungen.length === 0 ? <p className="text-sm text-[#8E7B71]" data-testid="offene-rechnungen-keine">Keine offenen Rechnungen.</p> : (
          <div className="divide-y divide-[#F6E3DA]">
            {rechnungen.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2 text-sm" data-testid={`offene-rechnung-${r.rechnungsnummer}`}>
                <button className="font-medium underline" onClick={() => rechnungPdf(r.id)}>{r.rechnungsnummer}</button>
                <span className="text-[#78716c]">vom {d(r.rechnungsdatum)}</span>
                <span className={r.zahlungsziel && r.zahlungsziel < heute ? "text-[#C2452F] font-medium" : "text-[#78716c]"}>fällig {d(r.zahlungsziel)}</span>
                {r.mahnstufe > 0 && <span className="text-xs rounded-full bg-[#FBE9E4] text-[#9A3B2E] px-2">Mahnstufe {r.mahnstufe}</span>}
                <span className="ml-auto font-semibold">{eur(r.offen)}<span className="text-xs font-normal text-[#8E7B71]"> von {eur(r.betrag)}</span></span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
